import { Pricing } from '@wix/ambassador-explore-catalog/types';
import { IHttpClient } from '@wix/http-client';
import { NextRouter } from 'next/router';
import { DEFAULT_CURRENCY, MAX_PRICE_IN_USD } from '../constants';
import {
  ENABLE_CURRENCY_PREFERENCES_EXPERIMENT,
  getStoredCurrencyPreferences,
  setStoredCurrencyPreferences,
  setCurrencyPreferencesRest,
} from './currencyPreferences';

export const USD_CURRENCY_CODE = 'USD';

export {
  ENABLE_CURRENCY_PREFERENCES_EXPERIMENT,
  getStoredCurrencyPreferences,
  setStoredCurrencyPreferences,
  setCurrencyPreferencesRest,
};

export const convertPricingToUSD = (
  pricing: Pricing,
  currencyConversionRate: number,
): Pricing => {
  return {
    min: convertUserCurrencyToUSD(currencyConversionRate, pricing.min),
    max: convertUserCurrencyToUSD(currencyConversionRate, pricing.max),
  };
};

const convertUserCurrencyToUSD = (
  currencyConversionRate: number,
  userCurrencyValue?: number,
) =>
  userCurrencyValue
    ? userCurrencyValue / currencyConversionRate
    : userCurrencyValue;

export const getCurrencyConvertedMaxPrice = (
  currencyConversionRate: number,
) => {
  const convertedMaxPrice = currencyConversionRate * MAX_PRICE_IN_USD;
  const scaleExponent = Math.floor(Math.log10(convertedMaxPrice));
  const scale = 10 ** scaleExponent;
  const roundedUnscaledValue = Math.round(convertedMaxPrice / scale);
  const roundedMaxPrice = roundedUnscaledValue * scale;
  return roundedMaxPrice;
};

export const formatCurrency = (
  language: string,
  amount: number = 0,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
) =>
  Intl.NumberFormat(language, {
    style: 'currency',
    currency: USD_CURRENCY_CODE,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);

interface UpdateCurrencyFromUrlParams {
  setCurrencyDispatch: (userCurrency: string, conversionRate: number) => void;
  preferredCurrency?: string;
  router: NextRouter;
  httpClient: IHttpClient;
  errorMonitor: any;
}

export async function updateCurrencyFromUrl({
  setCurrencyDispatch,
  preferredCurrency,
  router,
  httpClient,
  errorMonitor,
}: UpdateCurrencyFromUrlParams) {
  const resetToDefault = () => {
    return setCurrencyDispatch(DEFAULT_CURRENCY, 1);
  };

  const currencyParam = router.query.currency;
  let currencyISO = Array.isArray(currencyParam)
    ? currencyParam[0]
    : currencyParam;

  if (!currencyISO) {
    currencyISO = preferredCurrency;
  }

  if (currencyISO && currencyISO !== DEFAULT_CURRENCY) {
    try {
      const { data: conversionRate } = await httpClient.get<number>(
        `/api/currency/getCurrencyConversionRate?to=${currencyISO}`,
      );

      setCurrencyDispatch(currencyISO, conversionRate);
      return currencyISO;
    } catch (err) {
      resetToDefault();
      errorMonitor.captureException(err as Error);
    }
  }

  resetToDefault();
  return DEFAULT_CURRENCY;
}
